import {
  accountAndNadSearchMappings,
  accountSearchV2Mappings,
  accountServiceMappings,
} from '../accounts/accountServiceMappings';

import type {
  DynamicFormResponse,
  DynamicQuestionSetResponse,
} from '../form-engine/types';

import type { AccountDto, ProductType } from '../types';
import type { AccountTableInfoDto } from '../types/accounts/tables/account-table-info.dto';
import { mapCFKeys } from '../utility/CowbellFactorsMapping';
import {
  applyQueryConfig,
  serializeStandardResponse,
  ServiceHandler,
} from '../utils';
import { agencyLevelParamsSerializer } from './agency-level.params-serializer';
import { transformAgencyLevelResponse } from './agency-level.response-serializer';
import { standardQuerySerializer, type PageableResponse } from './serializers';
import { serializeAccountIndustryCode } from './serializers/industry-code.serializers';
import type { SelectOption } from '../console/agencies/quotes/PrimeX/customizeQuote/reducer/types';
import type { AgencySearchDto } from '../types/agencies/dto/agency-search.dto';
import type { SupportedProductLine } from '../components/inputs/selects/ProductLineSelect';

export const searchAccounts = ServiceHandler<
  unknown,
  PageableResponse<AccountTableInfoDto>
>({
  url: '/api/account/v1/account/search',
  paramsSerializer: (params) =>
    standardQuerySerializer(applyQueryConfig(accountServiceMappings, params)),
});

export const getAccountDetails = ServiceHandler<unknown, AccountDto>({
  url: '/api/account/v1/account/:accountId',
  transformResponse: [serializeStandardResponse, serializeAccountIndustryCode],
});

export const getAccountRiskDetails = ServiceHandler({
  url: '/api/risk/v1/:accountId',
  transformResponse: [serializeStandardResponse, mapCFKeys],
});

export const regenerateAgencyBill = ServiceHandler({
  url: '/api/account/v1/account/agencyBill',
});

export const getOwnershipTypes = ServiceHandler<
  unknown,
  Array<SelectOption<string>>
>({
  url: '/api/account/v1/account/ownershipTypes',
});

export interface GetAgenciesResponse {
  content: AgencySearchDto[];
}

export const getAgencies = ServiceHandler<unknown, GetAgenciesResponse>({
  url: 'api/account/v1/agency/search',
});

export interface GetAgencyLevelListingResponse {
  content: AgencyLevelListingTableRow[];
  totalElements: number;
}

export interface AgencyLevelListingTableRow {
  created: string;
  createdBy: string;
  id: string;
  lastModifiedBy: string;
  levelName: string;
  modified: string;
  qualifiers: RevenueBand[];
  ui_revenueBands: UIRevenueBand[];
}

export interface UIRevenueBand {
  maxRevenue: string;
  minRevenue: string;
  primaryProductType: string;
  secondaryProductType: string;
}

export interface RevenueBand {
  maxRevenue: number;
  minRevenue: number;
  primaryProductType: string;
  secondaryProductType: string;
}

export const getAgencyLevelListing = ServiceHandler<
  void,
  GetAgencyLevelListingResponse
>({
  url: 'api/account/v1/agency/levels',
  paramsSerializer: (params) =>
    standardQuerySerializer(
      applyQueryConfig(agencyLevelParamsSerializer, params)
    ),
  transformResponse: [serializeStandardResponse, transformAgencyLevelResponse],
});

export interface AddEditAgencyLevelPayload {
  levelName: string;
  qualifiers: RevenueBand[];
}

export const addAgencyLevel = ServiceHandler<
  AddEditAgencyLevelPayload,
  unknown
>({
  url: 'api/account/v1/agency/levels',
  method: 'POST',
});

export const editAgencyLevel = ServiceHandler<
  AddEditAgencyLevelPayload,
  unknown
>({
  url: 'api/account/v1/agency/levels/:id',
  method: 'PUT',
});

export const deleteAgencyLevel = ServiceHandler({
  url: 'api/account/v1/agency/levels/:id',
  method: 'DELETE',
});
export const updateSecurityContact = ServiceHandler({
  url: '/api/account/v1/account/securityContact/:accoundId',
  method: 'POST',
});

export const searchAccountsV2 = ServiceHandler({
  url: '/api/account/v1/account/search/dashboard',
  paramsSerializer: (params) =>
    standardQuerySerializer(applyQueryConfig(accountSearchV2Mappings, params)),
});

export const searchAccountsAndNad = ServiceHandler({
  url: '/api/account/v1/account/search/accounts-and-nad',
  paramsSerializer: (params) =>
    standardQuerySerializer(
      applyQueryConfig(accountAndNadSearchMappings, params)
    ),
});

interface ValidateAddressPayload {
  country: string;
  address1: string;
  address2?: string;
  address3?: string;
  city: string;
  zipCode: string;
}

export interface ValidateAddressResponse {
  verificationStatus: string;
  address: Record<string, string>;
  errorFields: string[];
  localizedErrorFieldNames: string[];
}

/* used for prime on UK address currently. WIll be used for US address too in future */
export const validateAddress = ServiceHandler<
  ValidateAddressPayload,
  ValidateAddressResponse
>({
  url: '/api/account/v1/address/validate',
  method: 'POST',
});

/** One suggestion from the address type-ahead, already shaped like the form's own fields. */
export interface AddressSuggestion {
  label: string;
  address1: string;
  address2: string | null;
  city: string;
  state: string;
  zipCode: string;
  /**
   * How many secondary addresses — units — share this primary address.
   *
   * A plain address is `0`, not `1`: verified against the live API, where every
   * single-occupancy suggestion carries zero. Above one, the line stands for several addresses
   * and the reader still has to pick a unit.
   */
  entries: number;
}

export interface AddressAutocompleteResponse {
  suggestions: AddressSuggestion[];
}

/**
 * Suggests US addresses as they are typed.
 *
 * A proxy in front of Smarty, so the credentials stay server-side. It answers an empty list
 * rather than failing when Smarty is unreachable or the account has no autocomplete
 * subscription — so a caller treats "no suggestions" as the normal quiet case, not an error.
 */
export const getAddressSuggestions = ServiceHandler<
  unknown,
  AddressAutocompleteResponse
>({
  url: '/api/account/v1/address/autocomplete',
});

export const fetchAccountStats = ServiceHandler({
  url: 'api/account/v1/account/stats/status',
  paramsSerializer: (params) =>
    standardQuerySerializer(applyQueryConfig(accountServiceMappings, params)),
});

export const fetchQuickQuoteStats = ServiceHandler({
  url: 'api/account/v1/account/quick-quote-premium',
  paramsSerializer: (params) =>
    standardQuerySerializer(applyQueryConfig(accountServiceMappings, params)),
});

export interface GetAvailableProductsResponse {
  products: {
    id: string;
    key: ProductType;
    name: string;
    type: string;
    productLine: ProductLine;
    /** ISO 3166-1 alpha-2, e.g. US or GB. */
    country?: string;
    /** Switched on in mothership. Absent until the API sends it. */
    enabled?: boolean;
    /** Whether a broker can send the product's quotes to underwriting for review. Absent until the API sends it. */
    referralEnabled?: boolean;
  }[];
}

export type ProductLine = {
  id: string;
  created: string;
  key: string;
  modified: string;
  name: string;
};

export const getAvailableProducts = ServiceHandler<
  unknown,
  GetAvailableProductsResponse
>({
  url: '/api/account/v1/products',
});

export type GetProductLinesResponse = {
  productLines: {
    id: string;
    key: string;
    name: string;
    prefix: string;
  }[];
};

export const getProductLines = ServiceHandler<unknown, GetProductLinesResponse>(
  {
    url: '/api/account/v1/product-lines',
  }
);

export type SupportedProductsResponse = {
  cowbellEnv: string;
  supportedProductLines: SupportedProductLine[];
};

export const getSupportedProducts = ServiceHandler<
  unknown,
  SupportedProductsResponse
>({
  url: '/api/account/v1/submission/supported-products',
  method: 'GET',
});

export const getDynamicForm = ServiceHandler<unknown, DynamicFormResponse>({
  url: '/api/account/v1/questions/:accountId/forms/:formId',
});

export const getAccountForms = ServiceHandler<
  unknown,
  DynamicQuestionSetResponse
>({
  url: '/api/account/v1/questions/account/:accountId/:productKey/forms',
});

export const getSubmissionsDynamicForm = ServiceHandler<
  unknown,
  DynamicFormResponse
>({
  url: '/api/account/v1/questions/submission/:submissionId/forms/:formId',
});

export const getSubmissionsDynamicForms = ServiceHandler<
  unknown,
  DynamicQuestionSetResponse
>({
  url: '/api/account/v1/questions/submission/:submissionId/:productType/forms',
});

export const getQuoteDynamicFormId = ServiceHandler<
  unknown,
  DynamicQuestionSetResponse
>({
  url: '/api/account/v1/questions/quote/:quoteId/forms',
});

export const getQuoteDynamicForm = ServiceHandler<unknown, DynamicFormResponse>(
  {
    url: '/api/account/v1/questions/quote/:quoteId/forms/:formId',
  }
);

export const saveDynamicFormFieldsWithKeys = ServiceHandler({
  url: '/api/account/v1/questions/:accountId/fields/key',
  method: 'POST',
});

export const saveDynamicFormFields = ServiceHandler({
  url: '/api/account/v1/questions/:accountId/fields',
  method: 'POST',
});

export const saveSubmissionDynamicFormFields = ServiceHandler({
  url: '/api/account/v1/questions/submission/:submissionId/fields',
  method: 'POST',
});

export const saveQuoteDynamicFormFields = ServiceHandler({
  url: '/api/account/v1/questions/quote/:quoteId',
  method: 'PUT',
});

export const getInterestedAgenciesByAccount = ServiceHandler({
  url: '/api/account/v1/agency/:accountId/interested-agencies',
});
